/**
 * Pipeline settings — vive en localStorage del navegador.
 *
 *  - mining_rate_m3_per_min: cuánto ore (en m³) saca tu stripper por minuto.
 *      Venture ≈ 150, Procurer ≈ 300, Retriever ≈ 500, Covetor ≈ 700,
 *      Hulk ≈ 900, Mackinaw (ice) ≈ 600.
 *  - final_yield_pct: yield efectivo de tu reprocess. Default 50 = NPC base.
 *      Con skills 5/5 + implant +10% + outpost rigs ≈ 75-85%.
 *  - refining_tax_pct: 0 si tenés refinery propio (con standings),
 *      5 si refinás en NPC station, hasta 10 en outpost.
 *
 * Auto-save en cada cambio (mismo patrón que refinery + compression).
 */

const KEY = 'eve.pipeline.settings.v1'

export interface PipelineSettings {
  mining_rate_m3_per_min: number
  final_yield_pct: number
  refining_tax_pct: number
}

const DEFAULTS: PipelineSettings = {
  mining_rate_m3_per_min: 300,
  final_yield_pct: 50,
  refining_tax_pct: 5,
}

interface StoredShape extends PipelineSettings {
  _updated_at?: number
}

export function loadPipelineSettings(): PipelineSettings {
  if (typeof localStorage === 'undefined') return DEFAULTS
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return DEFAULTS
    const parsed = JSON.parse(raw) as StoredShape
    return {
      mining_rate_m3_per_min: clampInRange(
        parsed.mining_rate_m3_per_min,
        DEFAULTS.mining_rate_m3_per_min,
        0,
        100000,
      ),
      final_yield_pct: clampInRange(
        parsed.final_yield_pct,
        DEFAULTS.final_yield_pct,
        0,
        85,
      ),
      refining_tax_pct: clampInRange(
        parsed.refining_tax_pct,
        DEFAULTS.refining_tax_pct,
        0,
        50,
      ),
    }
  } catch {
    return DEFAULTS
  }
}

export function savePipelineSettings(s: PipelineSettings): void {
  if (typeof localStorage === 'undefined') return
  const toStore: StoredShape = {
    ...s,
    _updated_at: Date.now(),
  }
  localStorage.setItem(KEY, JSON.stringify(toStore))
}

export function pipelineSettingsUpdatedAt(): number | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredShape
    return typeof parsed._updated_at === 'number' ? parsed._updated_at : null
  } catch {
    return null
  }
}

function clampInRange(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.max(min, Math.min(max, value))
}

export const DEFAULT_PIPELINE_SETTINGS = DEFAULTS

// Presets de ships comunes — facilita el setup inicial.
export const SHIP_PRESETS: Array<{ label: string; m3_per_min: number }> = [
  { label: 'Venture (Strip Miner I)', m3_per_min: 150 },
  { label: 'Procurer (Mod Strip Miner II)', m3_per_min: 300 },
  { label: 'Retriever (Mod Strip Miner II + Mining Aug)', m3_per_min: 500 },
  { label: 'Covetor (T2 Strip Miner)', m3_per_min: 700 },
  { label: 'Hulk (T2 Strip Miner + boosts)', m3_per_min: 900 },
  { label: 'Mackinaw (Ice)', m3_per_min: 600 },
]
